import Banner from 'apollo-react/components/Banner';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { clearErrors, removeError } from '../actions/ErrorActions';
import { setShowLongLoadPageMessage } from '../actions/LongLoadPageActions';
import { clearFormErrorsAndWarnings } from '../actions/NotificationDialogActions';
import { removeSuccess } from '../actions/SuccessActions';
import { clearWarnings, toggleWarningsByRef } from '../actions/WarningActions';
import withTranslation from '../components/withTranslation';
import { DEFAULT_LONG_LOAD_PAGE_MESSAGE } from '../constants/Common';
import {
  BULK_EDIT_PAGE,
  CERTIFICATION_PAGE,
  ENTITY_DISPLAY,
  WORKFLOW_STEP,
} from '../constants/Urls';
import { WARNING_SOURCES } from '../constants/WarningsConstants';
import { selectSessionExpirationErrorCode } from '../selectors/AuthSelectors';
import * as domUtils from '../utils/domUtils';
import { isMobileDevice } from '../utils/windowDimensionsUtils';

const style = { zIndex: 4400 };
const routesWithErrorDisplay = [ENTITY_DISPLAY, BULK_EDIT_PAGE, WORKFLOW_STEP, CERTIFICATION_PAGE];

class NotificationDialog extends React.Component {
  successTimeOut = null;

  componentDidMount() {
    const { successes } = this.props;

    if (successes.length > 0) {
      this.handleSuccessTransitions();
    }
  }

  componentDidUpdate(prevProps) {
    const { location, clearFormErrorsAndWarnings, successes } = this.props;
    const locationChanged = prevProps.location !== location;
    const wasOnAFormPage = routesWithErrorDisplay.includes(prevProps.location.pathname);
    const goesToAFormPage = routesWithErrorDisplay.includes(location.pathname);

    if (locationChanged && (wasOnAFormPage || goesToAFormPage)) {
      clearFormErrorsAndWarnings();
    }

    if (prevProps.successes !== successes) {
      this.handleSuccessTransitions();
    }
  }

  componentWillUnmount() {
    const { clearFormErrorsAndWarnings } = this.props;

    clearFormErrorsAndWarnings();
  }

  handleSuccessTransitions() {
    const { successes, removeSuccess } = this.props;

    if (this.successTimeOut) {
      clearTimeout(this.successTimeOut);
    }

    this.successTimeOut =
      successes.length > 0
        ? setTimeout(() => {
            removeSuccess(_.last(successes));
          }, 3000)
        : null;
  }

  render() {
    const {
      errors,
      location,
      removeError,
      removeSuccess,
      sessionExpirationErrorCode,
      successes,
      warnings,
      translateString,
      toggleWarningsByRef,
      showLongLoadPageMessage,
      setShowLongLoadPageMessage,
      customLongLoadPageMessage,
    } = this.props;

    const isEntityDisplay = [ENTITY_DISPLAY, ENTITY_DISPLAY].includes(location.pathname);

    const pageHasErrorDisplay =
      routesWithErrorDisplay.includes(location.pathname) && (!isMobileDevice() || isEntityDisplay);

    const errorsOnTop = pageHasErrorDisplay
      ? errors.filter(
          ({ code, isTopBanner }) =>
            code === sessionExpirationErrorCode || code === '50001' || isTopBanner
        )
      : errors;

    const warningsOnTop = pageHasErrorDisplay
      ? warnings.filter(
          (warning) =>
            warning.source === WARNING_SOURCES.SERVER ||
            warning.source === WARNING_SOURCES.ASSISTANT ||
            warning.source === WARNING_SOURCES.ARCHIVE_MODE ||
            warning.source === WARNING_SOURCES.OFFLINE_MODE
        )
      : warnings;

    const reversedErrors = [...errorsOnTop].reverse();
    const reversedWarnings = [...warningsOnTop].reverse();

    return (
      <>
        {showLongLoadPageMessage && (
          <Banner
            key={9999}
            open
            onClose={() => {
              setShowLongLoadPageMessage(false);
            }}
            message={translateString(customLongLoadPageMessage || DEFAULT_LONG_LOAD_PAGE_MESSAGE)}
            style={style}
            variant="info"
          />
        )}
        {successes.map((success, index) => {
          return (
            <Banner
              key={index}
              open
              onClose={() => {
                removeSuccess(success);
              }}
              message={success.message}
              style={style}
              variant="success"
            />
          );
        })}
        {reversedErrors.map((error, index) => {
          return (
            <Banner
              key={index}
              open
              onClose={() => removeError(error)}
              message={`${error.message} ${
                error.code ? ` ${translateString('Error code')} : ${error.code}` : ''
              }`}
              style={style}
              variant="error"
            />
          );
        })}
        {reversedWarnings.map((warning, index) => {
          return (
            <Banner
              key={index}
              open
              onClose={() => toggleWarningsByRef(warning, false)}
              onClick={isMobileDevice() ? () => domUtils.handleWarningClick(warning) : null}
              message={warning.message}
              style={style}
              variant="warning"
            />
          );
        })}
      </>
    );
  }
}

/**
 * @param state
 */
function mapStateToProps(state) {
  const { successes, errors, warnings, longLoadPageMessage } = state;

  return {
    successes,
    errors,
    warnings: warnings.filter((warning) => warning.warningEnabled),
    sessionExpirationErrorCode: selectSessionExpirationErrorCode(state),
    showLongLoadPageMessage: longLoadPageMessage.showMessage,
    customLongLoadPageMessage: longLoadPageMessage.customMessage,
  };
}

/**
 * @param dispatch
 * @returns {{actions}}
 */
function mapDispatchToProps(dispatch) {
  return {
    removeSuccess: (success) => {
      dispatch(removeSuccess(success));
    },
    removeError: (error) => {
      dispatch(removeError(error));
    },
    clearErrors: () => {
      dispatch(clearErrors());
    },
    clearWarnings: () => {
      dispatch(clearWarnings());
    },
    clearFormErrorsAndWarnings: () => {
      dispatch(clearFormErrorsAndWarnings());
    },
    toggleWarningsByRef: (warning, flag) => {
      dispatch(toggleWarningsByRef(warning, flag));
    },
    setShowLongLoadPageMessage: (flag) => {
      dispatch(setShowLongLoadPageMessage(flag));
    },
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withTranslation(NotificationDialog))
);
